import { FC } from "react";
import { Box, styled, useTheme } from "@mui/system";
import muiIcons from "../../../components/icons/mui-icons";
import Containers from "../../../components/Containers";
import GullTable from "../../../components/tables/GullTable";
import PieChart from "../../../components/apexCharts/PieChart";
import { CardContainer } from "../../../components/CardContainer";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import { GullButton } from "../../../components/buttons/GullButton";
import BasicLineChart1 from "../../../components/apexCharts/BasicLineChart1";
import BasicAreaChart from "../../../components/apexCharts/BasicAreaChart";
import ComparisonChart from "../../../components/apexCharts/ComparisonChart";
import {
  H2,
  H3,
  H4,
  H5,
  Paragraph,
  Span,
} from "../../../components/Typography";
import {
  tableDataList,
  userActivityList,
  productCardData,
  topTitleCardData,
} from "./dashboard1-data";
import {
  Avatar,
  Grid,
  IconButton,
  Paper,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";

const TableCardHeader = styled(Box)(({ theme }) => ({
  padding: "1rem",
  display: "flex",
  alignItems: "center",
  borderRadius: "10px 10px 0px 0px",
  justifyContent: "space-between",
  "& h2": {
    fontsize: "24px",
    marginTop: "1rem",
    color: theme.palette.primary.main,
  },
}));

const IconBox = styled(Box)(() => ({
  background: "#6b339921",
  height: "48px",
  width: "48px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
}));

const ProductBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: "3px 0px",
  justifyContent: "space-between",
  "& p": {
    fontSize: 13,
    color: theme.palette.grey[600],
  },
  "& h4": {
    marginBottom: "5px",
    color: theme.palette.primary.main,
  },
  [theme.breakpoints.down("sm")]: {
    display: "block",
    padding: "15px 0px",
  },
}));

const StyledBox = styled(Box)(() => ({
  margin: "auto",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
}));

const ProductBody = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  [theme.breakpoints.down("sm")]: {
    display: "block",
  },
}));

const ProductImg = styled("img")(() => ({
  width: "80px",
  height: "80px",
  marginRight: 16,
  borderRadius: 5,
}));

const Status = styled(Span)<{ status: string }>(({ theme, status }) => ({
  fontSize: 11,
  borderRadius: 5,
  padding: "1px 5px",
  color:
    status === "active"
      ? theme.palette.grey[100]
      : status === "pending" && theme.palette.grey[100],
  background:
    status === "active"
      ? theme.palette.success.main
      : status === "pending"
      ? theme.palette.primary.main
      : status === "inactive" && theme.palette.warning.main,
}));

const Dashboard1: FC = () => {
  const theme = useTheme();
  console.log(theme.palette.mode);
  const { AddUserIcon, CloseIcon, EditIcon } = muiIcons;
  const { ChartContainer, TableContainer } = Containers();

  return (
    <>
      <Breadcrumb name="Dashboard" subName="Version 1" path="/dashboard/v1" />

      <Grid container spacing={3}>
        {topTitleCardData.map((data) => {
          const { Icon, title, count } = data;
          return (
            <Grid item xs={12} sm={6} md={3}>
              <CardContainer sx={{ display: "flex" }}>
                <IconBox>
                  <Icon
                    sx={{
                      fontSize: "1.2rem",
                      color: "primary.main",
                    }}
                  />
                </IconBox>
                <StyledBox>
                  <Paragraph fontSize=".8rem">{title}</Paragraph>
                  <H2 fontSize="1.25rem" color="primary.main">
                    {count}
                  </H2>
                </StyledBox>
              </CardContainer>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={3} my="0px">
        <Grid item xs={12} md={8}>
          <ChartContainer sx={{ height: 360 }}>
            <H3>This Year Sales</H3>
            <ComparisonChart />
          </ChartContainer>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartContainer sx={{ height: 360 }}>
            <H3 mb="40px">Sales By Countries</H3>
            <PieChart />
          </ChartContainer>
        </Grid>
      </Grid>

      <Grid container spacing={3} my="0px">
        <Grid item xs={12} md={6}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ChartContainer sx={{ p: 0, height: 360 }}>
                <TableCardHeader>
                  <Box>
                    <H3>Last Month Sales</H3>
                    <H2>$40250</H2>
                  </Box>
                </TableCardHeader>
                <BasicAreaChart
                  height={270}
                  color={theme.palette.primary.main}
                  data={[30, 40, 20, 50, 40, 80, 90]}
                />
              </ChartContainer>
            </Grid>

            <Grid item xs={12} md={6}>
              <ChartContainer sx={{ p: 0, height: 360 }}>
                <TableCardHeader>
                  <Box>
                    <H3>Last Month Sales</H3>
                    <H2>$10250</H2>
                  </Box>
                </TableCardHeader>
                <BasicAreaChart
                  height={270}
                  color={theme.palette.warning.main}
                  data={[30, 10, 40, 10, 40, 20, 90]}
                />
              </ChartContainer>
            </Grid>
          </Grid>

          <Grid item xs={12} my={3}>
            <TableContainer sx={{ p: 0, pb: 1, mt: "25px" }}>
              <TableCardHeader
                sx={{
                  p: ".6rem 1rem",
                  background:
                    theme.palette.mode === "light" && theme.palette.grey[50],
                }}
              >
                <H3>New Users</H3>
                <IconButton>
                  <AddUserIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </TableCardHeader>

              <GullTable>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">#</TableCell>
                    <TableCell align="center">Name</TableCell>
                    <TableCell align="center">Avatar</TableCell>
                    <TableCell align="center">Email</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableDataList.map((user: any, index) => {
                    const { name, photoUrl, email, status } = user;
                    return (
                      <TableRow hover id="GullTableRow" key={index}>
                        <TableCell align="center">{index + 1}</TableCell>
                        <TableCell align="center">{name}</TableCell>
                        <TableCell align="center">
                          <Avatar
                            src={photoUrl}
                            alt="Remy Sharp"
                            sx={{ width: 24, height: 24, margin: "auto" }}
                          />
                        </TableCell>
                        <TableCell align="center">{email}</TableCell>
                        <TableCell align="center">
                          <Status status={status}>{status}</Status>
                        </TableCell>
                        <TableCell align="center">
                          <EditIcon
                            sx={{
                              mx: "3px",
                              mb: "-2px",
                              color: "success.main",
                              cursor: "pointer",
                            }}
                          />
                          <CloseIcon
                            sx={{
                              mx: "3px",
                              color: "red",
                              fontSize: 15,
                              cursor: "pointer",
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </GullTable>
            </TableContainer>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <CardContainer>
            {productCardData.map((product, index) => {
              const { title, description, prevPrice, currentPrice, imgUrl } =
                product;
              return (
                <ProductBox key={index}>
                  <ProductImg src={imgUrl} alt="" />
                  <ProductBody>
                    <Box my="10px">
                      <H4>{title}</H4>
                      <Paragraph sx={{ my: "5px" }}>{description}</Paragraph>
                      <Paragraph>
                        <Span color="red">{`$${prevPrice}`}</Span>{" "}
                        <Span sx={{ textDecoration: "line-through" }}>
                          {`$${currentPrice}`}
                        </Span>
                      </Paragraph>
                    </Box>
                    <GullButton
                      rounded={true}
                      variant="outlined"
                      color="primary"
                      sx={{ p: ".2rem 1rem" }}
                    >
                      View details
                    </GullButton>
                  </ProductBody>
                </ProductBox>
              );
            })}
          </CardContainer>

          <CardContainer sx={{ p: 0, mt: "25px" }}>
            <TableCardHeader>
              <H3>User Activity</H3>
              <Span
                sx={{
                  background: "#ffc107",
                  borderRadius: 5,
                  fontWeight: 600,
                  padding: "0px 10px",
                }}
              >
                Updated daily
              </Span>
            </TableCardHeader>
            {userActivityList.map(({ activitylist = [] }, index) => (
              <Grid
                p="1rem"
                container
                borderTop={`1px solid ${theme.palette.grey[300]}`}
              >
                {activitylist.map((item, i) => (
                  <Grid key={i} item xs={4}>
                    <Span>{item.title}</Span>
                    <H5>{item.count}</H5>
                  </Grid>
                ))}
              </Grid>
            ))}
          </CardContainer>
        </Grid>

        <Grid item xs={12}>
          <ChartContainer sx={{ height: 360 }}>
            <H3>Last 20 Day Leads</H3>
            <BasicLineChart1
              data={[
                40, 80, 20, 90, 30, 80, 40, 90, 20, 80, 30, 45, 50, 110, 90,
                145, 120, 135, 120, 140,
              ]}
              color={theme.palette.primary.main}
              height={300}
            />
          </ChartContainer>
        </Grid>
      </Grid>
    </>
  );
};

export default Dashboard1;
